import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { usePurchaseCredits } from '../../hooks/useCredits';

interface OutOfCreditsModalProps {
  open: boolean;
  onClose: () => void;
}

export function OutOfCreditsModal({ open, onClose }: OutOfCreditsModalProps) {
  const purchaseCredits = usePurchaseCredits();

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="credits-modal-title">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />

      <div className="relative z-10 w-full max-w-md rounded-xl border border-border bg-card p-6 shadow-2xl">
        <button type="button" onClick={onClose}
          className="absolute right-4 top-4 text-muted-foreground hover:text-foreground transition-colors"
          aria-label="Close">✕</button>

        <div className="mb-4 text-3xl">💳</div>

        <h2 id="credits-modal-title" className="text-xl font-bold text-foreground mb-2">
          You're out of credits
        </h2>
        <p className="text-sm text-muted-foreground mb-6">
          Creating a new book costs 1 credit. Top up your balance or upgrade your plan to continue writing.
        </p>

        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={() => purchaseCredits.mutate()}
            disabled={purchaseCredits.isPending}
            className="w-full rounded-lg bg-primary px-4 py-2.5 text-center text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
          >
            {purchaseCredits.isPending ? 'Loading…' : 'Buy 10 Credits — $9.99'}
          </button>
          <Link
            to="/pricing"
            className="w-full rounded-lg border border-border px-4 py-2.5 text-center text-sm font-medium text-foreground hover:bg-accent transition-colors"
          >
            View Plans →
          </Link>
          <button type="button" onClick={onClose}
            className="w-full rounded-lg px-4 py-2.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
            Maybe Later
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
